/**
 * EnvironmentBadge Component
 * Displays a color-coded environment indicator
 * Constitution v1.8.0 - Multi-Subscription SQL MI Edition
 *
 * Colors:
 * - DEV: Green (bg-green-500)
 * - UAT: Amber (bg-amber-500)
 * - PROD: Red (bg-red-600) with elevated visual weight
 */

import React from 'react';

export interface EnvironmentBadgeProps {
  environment: 'development' | 'uat' | 'production';
  details?: {
    database: string;
    sqlMi: string;
    keyVault?: string;
  };
  size?: 'sm' | 'md' | 'lg';
}

const environmentConfig = {
  development: {
    label: 'DEV',
    bgClass: 'bg-green-500',
    textClass: 'text-white',
    hoverClass: 'hover:bg-green-600',
  },
  uat: {
    label: 'UAT',
    bgClass: 'bg-amber-500',
    textClass: 'text-black',
    hoverClass: 'hover:bg-amber-600',
  },
  production: {
    label: 'PROD',
    bgClass: 'bg-red-600',
    textClass: 'text-white font-bold',
    hoverClass: 'hover:bg-red-700',
  },
};

const sizeConfig = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({
  environment,
  details,
  size = 'md',
}) => {
  const config = environmentConfig[environment] || environmentConfig.development;
  const sizeClass = sizeConfig[size];

  // Build tooltip text if details provided
  const tooltipText = details
    ? `Database: ${details.database}\nSQL MI: ${details.sqlMi}${details.keyVault ? `\nKey Vault: ${details.keyVault}` : ''}`
    : undefined;

  return (
    <span
      className={`
        inline-flex items-center rounded-md
        ${config.bgClass}
        ${config.textClass}
        ${config.hoverClass}
        ${sizeClass}
        transition-colors cursor-default
        ${environment === 'production' ? 'ring-2 ring-red-300 ring-opacity-50' : ''}
      `}
      title={tooltipText}
      role='status'
      aria-label={`Current environment: ${config.label}`}
    >
      {config.label}
    </span>
  );
};

export default EnvironmentBadge;
